import { useState } from "react";

import AddMedicationModal from "./AddMedicationModal";
import MedicationCard from "./MedicationCard";
import SummaryEmptyState from "./SummaryEmptyState";

import type { AddMedicationPreset } from "../../hooks/ai-insights/useMedicationEditor";
import type {
  EditableMedicationValue,
  MedicationField,
} from "../../utils/codeMetadataUtils";

type MedicationsPanelProps = {
  medications: EditableMedicationValue[];
  emptyMessage: string;
  sourceContext?: {
    contextTitle: string;
    contextValue: string;
  };
  isCollapsed: boolean;
  isAddingMedication: boolean;
  canAddMedication: boolean;
  onToggleCollapse: () => void;
  onAddMedication: (preset: AddMedicationPreset) => Promise<void>;
  onFieldChange: (
    metadataId: number,
    field: MedicationField,
    value: string,
  ) => void;
  onFieldSave: (
    metadataId: number,
    field: MedicationField,
    value: string,
  ) => Promise<void>;
};

/*
renders structured medication cards for the active document group
mirrors the collapsible ai summary shell used by codes
*/
const MedicationsPanel = ({
  medications,
  emptyMessage,
  sourceContext,
  isCollapsed,
  isAddingMedication,
  canAddMedication,
  onToggleCollapse,
  onAddMedication,
  onFieldChange,
  onFieldSave,
}: MedicationsPanelProps) => {
  const [isAddMedicationModalOpen, setIsAddMedicationModalOpen] =
    useState(false);
  const hasMedications = medications.length > 0;

  return (
    <>
      <div
        className={`metadoc-ai-summary metadoc-ai-summary--visible ${isCollapsed ? "metadoc-ai-summary--collapsed" : ""}`}
      >
        <div className="metadoc-ai-summary__header">
          <div className="metadoc-ai-summary__badge">
            <span className="material-icons">auto_awesome</span>
            <span className="metadoc-ai-summary__badge-text">
              AI Generated Medications
            </span>
          </div>
          <div className="metadoc-ai-summary__header-actions">
            <button
              type="button"
              className="metadoc-ai-summary__context"
              onClick={() => {
                setIsAddMedicationModalOpen(true);
              }}
              disabled={!canAddMedication || isAddingMedication}
              title="Add medication"
            >
              <span className="metadoc-ai-summary__context-title">
                Medication
              </span>
              <span className="metadoc-ai-summary__context-value">Add+</span>
            </button>
            {sourceContext && (
              <div className="metadoc-ai-summary__context">
                <span className="metadoc-ai-summary__context-title">
                  {sourceContext.contextTitle}
                </span>
                <span className="metadoc-ai-summary__context-value">
                  {sourceContext.contextValue}
                </span>
              </div>
            )}
            <button
              type="button"
              className="metadoc-ai-summary__collapse-btn"
              onClick={onToggleCollapse}
              title={
                isCollapsed ? "Expand medications" : "Collapse medications"
              }
            >
              <span className="material-icons">
                {isCollapsed ? "expand_more" : "expand_less"}
              </span>
            </button>
          </div>
        </div>
        {!isCollapsed && (
          <div
            className={`metadoc-viewer-workspace__medications-panel ${!hasMedications ? "metadoc-viewer-workspace__medications-panel--empty" : ""}`}
          >
            {!hasMedications && <SummaryEmptyState message={emptyMessage} />}
            {hasMedications &&
              medications.map((medication) => (
                <MedicationCard
                  key={medication.id}
                  medication={medication}
                  onFieldChange={onFieldChange}
                  onFieldSave={onFieldSave}
                />
              ))}
          </div>
        )}
      </div>
      <AddMedicationModal
        isOpen={isAddMedicationModalOpen}
        isSubmitting={isAddingMedication}
        onClose={() => {
          setIsAddMedicationModalOpen(false);
        }}
        onSubmit={async (preset) => {
          await onAddMedication(preset);
          setIsAddMedicationModalOpen(false);
        }}
      />
    </>
  );
};

export default MedicationsPanel;
